<template>
  <div
    ref="gridRef"
    class="ui-grid"
    :class="mainClasses"
  >
    <slot name="top" />
    <div
      ref="containerRef"
      class="ui-grid__container"
      :class="containerClasses"
      :style="containerStyles"
      @mousemove="handleContainerMouseMove"
      @mouseleave="clearInsertButton"
    >
      <template v-if="isHeaderVisible">
        <Header
          v-if="selectable"
          is-first
        >
          <slot name="headerCheckbox">
            <Checkbox
              v-model="checked"
              v-bind="{ indeterminate }"
              @change="handleSelectAll"
            />
          </slot>
        </Header>
        <Header
          v-for="(column, index) in outputColumns"
          :key="column.name"
          v-model:sort="sort"
          v-bind="{ column, isHeaderChecked }"
          :is-first="!selectable && index === 0"
          :is-last="index === outputColumns.length - 1"
          @resize="hadnleColumnResize"
          @reset="handleColumnReset"
        >
          <template #default="slotProps">
            <slot
              v-if="$slots[getSlotName(column.name, 'header')]"
              :name="getSlotName(column.name, 'header')"
              v-bind="slotProps"
            />
            <slot
              v-else
              name="header"
              v-bind="slotProps"
            />
          </template>
        </Header>
      </template>
      <template v-if="areThereResult">
        <Row
          v-for="(item, index) in outputItems"
          :ref="el => setRowRef(el, item, index)"
          :key="getKeyValue(item, index)"
          v-model:selected="selected"
          v-bind="{ item, template, isCollapsed }"
          :row-index="index"
          :row-actions
          :columns="outputColumns"
          :active="getKeyValue(item, index) === activeId"
          :edit-row="
            editableRows
              ? getKeyValue(item, index) === `${editRowId}`
              : undefined
          "
          :edit="index + 1 === edit.row ? edit : undefined"
          :item-key="getKeyValue(item, index)"
          @click:value="handleClickValue"
          @click:row="handleClickRow(item, index)"
          @dblclick:cell="handleDblClickCell"
          @click:menu="handleClickMenu"
        >
          <template #prefix="slotProps">
            <slot
              name="prefix"
              v-bind="slotProps"
            />
          </template>
          <template #checkbox="slotProps">
            <slot
              name="checkbox"
              v-bind="slotProps"
            />
          </template>
          <template #actions="slotProps">
            <slot
              name="actions"
              v-bind="slotProps"
            />
          </template>
          <!-- @vue-ignore -->
          <template
            v-for="(_, slot) in $slots"
            #[slot]="slotProps"
            :key="`${slot}-${index}`"
          >
            <slot
              :name="slot"
              v-bind="slotProps"
            />
          </template>
        </Row>
      </template>
      <InsertRowButton
        v-if="isInsertButtonVisible"
        :top="insertButtonTop"
        @click="handleClickInsertRow"
      />
      <template v-if="isFooterVisible">
        <Footer
          v-if="selectable"
          is-first
        />
        <Footer
          v-for="(column, index) in outputColumns"
          :key="column.name"
          v-bind="{ column, footer }"
          :is-first="!selectable && index === 0"
          :is-last="index === outputColumns.length - 1"
        >
          <template #default="slotProps">
            <slot
              v-if="$slots[getSlotName(column.name, 'footer')]"
              :name="getSlotName(column.name, 'footer')"
              v-bind="slotProps"
            />
            <slot
              v-else
              name="footer"
              v-bind="slotProps"
            />
          </template>
        </Footer>
      </template>
      <div
        v-if="isLazyIndicatorVisible"
        ref="lazyRef"
        class="ui-grid__lazy"
      />
    </div>
    <EmptyResult v-if="!areThereResult" />
    <slot name="bottom" />
  </div>
</template>

<script setup lang="ts" generic="T extends GridItem">
import {
  computed,
  onBeforeMount,
  onMounted,
  onBeforeUnmount,
  provide,
  reactive,
  ref,
  useTemplateRef,
  watch,
} from 'vue'
import {
  onClickOutside,
  useActiveElement,
  useElementVisibility,
  useMagicKeys,
  useResizeObserver,
} from '@vueuse/core'
import { isEqual, min } from 'lodash'
import { Capacitor } from '@capacitor/core'

import { DottedMenuItem, Sort } from '@types'
import {
  GridColumn,
  GridColumnWidth,
  GridEdit,
  GridFooter,
  GridItem,
  GridValueCopier,
  GridValueGetter,
  GridValuePaster,
} from './utils/types'

import { COLLAPSE_WIDTH } from './utils/const'

import {
  getSlotName,
  getTemplateColumns,
  pasteFromClipboard,
} from './utils/helpers'

import useSelectionService from '@/components/hooks/selection'

import { useResponsive } from '@/plugins/responsiveUI'

import Checkbox from './components/Checkbox.vue'
import EmptyResult from './components/EmptyResult.vue'
import Footer from './components/Footer.vue'
import Header from './components/Header.vue'
import InsertRowButton from './components/InsertRowButton.vue'
import Row from './components/Row.vue'

type Props = {
  columns: GridColumn[]
  items: T[]

  footer?: GridFooter

  disabled?: boolean

  idField?: keyof T
  activeId?: string | number
  editRowId?: string | number

  sm?: string

  wrapped?: boolean

  scrollable?: boolean
  selectable?: boolean
  editable?: boolean
  editableRows?: boolean

  readonly?: boolean

  unitarySort?: boolean

  displayMenu?: boolean

  collapseWidth?: number

  lazy?: number

  valueGetter?: GridValueGetter
  valuePaster?: GridValuePaster
  valueCopier?: GridValueCopier

  rowActions?: DottedMenuItem[]
  insertableRows?: boolean
}

type Emits = {
  'click:menu': [data: T]
  'click:row': [data: T, index: number]
  'click:value': [data: T, index: number]
  'dblclick:cell': [data?: T, index?: number]
  'click:insert-row': [index: number]
  pasted: []
}

const {
  idField = 'id' as keyof T,
  collapseWidth = COLLAPSE_WIDTH,
  ...props
} = defineProps<Props>()
const emit = defineEmits<Emits>()

let selectionService: ReturnType<typeof useSelectionService>

const exposeObj = {
  getContainer() {
    return containerRef.value
  },
  clickTab() {
    selectionService?.selectNext()
    return exposeObj
  },
  clickEnter() {
    selectionService?.selectBellow()
    return exposeObj
  },
  deselect() {
    checked.value = false
    selected.value = []
    return exposeObj
  },
  release() {
    release()
    return exposeObj
  },
  // shift the lazy start if needed
  lazyShift(count: number) {
    if (count < itemsPerPage.value) return
    itemsPerPage.value += count
    return exposeObj
  },
  edit(row: number, cell: number) {
    edit.cell = cell
    edit.row = row
    return exposeObj
  }
}

defineExpose(exposeObj)

const { isTablet, isDesktop } = useResponsive()

const sort = defineModel<Sort[]>('sort')
const selected = defineModel<(string | number)[]>('selected')
const externalColumnsWidth = defineModel<GridColumnWidth>('width')

defineOptions({
  name: 'UIGrid',
})

const gridRef = useTemplateRef('gridRef')
const gridWidth = ref(0)

useResizeObserver(gridRef, entries => {
  const { width } = entries[0].contentRect
  gridWidth.value = width
})

const isCollapsed = computed(
  () => gridWidth.value < collapseWidth || Capacitor.isNativePlatform(),
)

const hasRowMenu = computed(() => props.displayMenu)

provide('disabled', props.disabled)
provide('unitarySort', props.unitarySort)
provide('hasRowMenu', hasRowMenu)
provide('selectable', props.selectable)
provide('editable', props.editable)
provide('valueGetter', props.valueGetter)
provide('valuePaster', props.valuePaster)
provide('valueCopier', props.valueCopier)
provide('isReadonly', props.readonly)
provide('isCollapsed', isCollapsed)

const containerRef = useTemplateRef('containerRef')
const rowRef = ref<Record<string, any>>({})

const lazyRef = useTemplateRef('lazyRef')
const isScrolledDown = useElementVisibility(lazyRef)

const internalColumnsWidth = ref<GridColumnWidth>({})

const itemsPerPage = ref(0)

const checked = ref(false)

const edit = reactive<GridEdit>({
  cell: undefined,
  row: undefined,
})

const disableSelection = computed(
  () => edit.cell !== undefined && edit.row !== undefined,
)

const canEditCells = computed(
  () => !!props.editable && !props.readonly && !props.disabled,
)

const silentColumnsCount = props.columns.filter(column => column.silent).length

if (!props.disabled) {
  const selectionShift = Number(props.selectable) + silentColumnsCount
  selectionService = useSelectionService(
    containerRef,
    selectionShift,
    disableSelection,
    release,
  )
}

const areThereResult = computed(() => !!outputItems.value.length)

const columnsWidth = computed({
  get() {
    return externalColumnsWidth.value || internalColumnsWidth.value
  },
  set(value) {
    internalColumnsWidth.value = value
    externalColumnsWidth.value = value
  },
})

const outputColumns = computed(() =>
  props.columns.filter(column => !column.hidden),
)

const outputItems = computed<T[]>(oldValue => {
  let result
  if (itemsPerPage.value) {
    result = props.items.slice(0, itemsPerPage.value)
  } else {
    result = props.items
  }
  if (isEqual(oldValue, result)) {
    return oldValue || []
  } else {
    return result
  }
})

const indeterminate = computed(
  () =>
    props.selectable &&
    selected.value &&
    selected.value.length !== outputItems.value.length &&
    selected.value.length !== 0,
)

const isHeaderChecked = computed(() => checked.value || indeterminate.value)

const currentTemplate = computed(() => {
  let width: keyof GridColumn | undefined = undefined
  if (isTablet.value) {
    width = 'md'
  } else if (isDesktop.value) {
    width = 'lg'
  }
  return outputColumns.value
    .map(column => `${(width && column[width]) || column.default || '1fr'} `)
    .join('')
})
const template = computed(() =>
  isCollapsed.value ? currentTemplate.value : undefined,
)

const isHeaderVisible = computed(() => !isCollapsed.value)
const isFooterVisible = computed(
  () => props.footer && !isCollapsed.value && areThereResult.value,
)

const isLazyIndicatorVisible = computed(
  () => props.lazy && itemsPerPage.value < props.items.length,
)

const insertIndex = ref<number>()
const insertButtonTop = ref<number>()

const isInsertButtonAvailable = computed(
  () =>
    !!props.insertableRows &&
    !props.readonly &&
    !props.disabled &&
    !isCollapsed.value,
)

const isInsertButtonVisible = computed(
  () =>
    isInsertButtonAvailable.value &&
    insertIndex.value !== undefined &&
    insertButtonTop.value !== undefined,
)

const containerStyles = computed(() =>
  isCollapsed.value
    ? undefined
    : getTemplateColumns(
      outputColumns.value,
      columnsWidth.value,
      currentTemplate.value,
      props.selectable,
    ),
)

const mainClasses = computed(() => ({
  'ui-grid--wrapped': props.wrapped,
  'ui-grid--collapsed': isCollapsed.value,
}))

const containerClasses = computed(() => ({
  'ui-grid__container--scrollable': props.scrollable && areThereResult.value,
  'ui-grid__container--no-result': !areThereResult.value,
}))

const handleSelectAll = (flag: boolean) => {
  if (indeterminate.value || !flag) {
    selected.value = []
    checked.value = false
  } else {
    selected.value = Object.values(rowRef.value)?.reduce((acc, item) => {
      const itemKey = item?.getCheckableItemKey()
      itemKey && acc.push(itemKey)
      return acc
    }, [])
    checked.value = true
  }
}

const handleClickMenu = (data: T) => {
  emit('click:menu', data)
}

const handleClickRow = (data: T, index: number) => {
  emit('click:row', data, index)
}

const handleClickValue = (data: T, index: number) => {
  emit('click:value', data, index)
}

const clearInsertButton = () => {
  insertIndex.value = undefined
  insertButtonTop.value = undefined
}

const handleContainerMouseMove = (event: MouseEvent) => {
  if (!isInsertButtonAvailable.value) {
    clearInsertButton()
    return
  }

  const insertButton = (event.target as HTMLElement)?.closest?.('.ui-grid__insert-button')
  if (insertButton) return

  const cell = (event.target as HTMLElement)?.closest?.('.ui-grid__cell[data-row-index]') as HTMLElement | null
  if (!cell) {
    clearInsertButton()
    return
  }

  const rowIndex = Number(cell.dataset.rowIndex)
  if (Number.isNaN(rowIndex)) {
    clearInsertButton()
    return
  }

  const rect = cell.getBoundingClientRect()
  const containerRect = containerRef.value?.getBoundingClientRect()
  if (!containerRect) {
    clearInsertButton()
    return
  }

  const isTopHalf = event.clientY <= rect.top + rect.height / 2
  insertIndex.value = isTopHalf ? rowIndex : rowIndex + 1
  insertButtonTop.value = isTopHalf
    ? rect.top - containerRect.top
    : rect.bottom - containerRect.top
}

const handleClickInsertRow = () => {
  if (insertIndex.value === undefined) return
  emit('click:insert-row', insertIndex.value)
}

const hadnleColumnResize = (column: string, width: number) => {
  columnsWidth.value[column] = width
}

const handleColumnReset = (column: string) => {
  columnsWidth.value[column] = 0
}

const calculateSelectIndexes = (editCell: number) => {
  const columnsLength = outputColumns.value.length
  const diff = editCell % (columnsLength - silentColumnsCount)
  const cell = silentColumnsCount ? diff + silentColumnsCount : diff
  const row = (editCell - diff) / (columnsLength - silentColumnsCount)
  return { cell, row }
}

const handleDblClickCell = (data?: T, index?: number) => {
  emit('dblclick:cell', data, index)
  if (!canEditCells.value || !selectionService.hasSelected.value) return
  const range = selectionService.range
  const editCell = min(Object.values(range)) || 0
  if (!editCell) return
  const { cell, row } = calculateSelectIndexes(editCell)
  if (!props.editable || outputColumns.value[cell].uneditable) return
  edit.cell = cell
  edit.row = row
}

const getKeyValue = (item: T, index: number) =>
  (idField && `${item?.[idField as keyof T]}`) || index

const setRowRef = (el: any, item: T, index: number) => {
  rowRef.value[getKeyValue(item, index)] = el
}

const getRowRef = (rowIndex: number) => {
  const index = rowIndex - 1
  const item = outputItems.value[index]
  const key = getKeyValue(item, index) as string
  return rowRef.value?.[key]
}

const handlePaste = (event: ClipboardEvent) => {
  if ((edit.cell && edit.row) || !selectionService?.hasSelected.value) return
  const pasteData = pasteFromClipboard(event)
  if (!pasteData.length || selectionService.range.start === undefined) return

  const { rangeStart, rangeEnd, cols, step } = selectionService.getRangeCells()
  const { cell: startCell, row: startRow } = calculateSelectIndexes(rangeStart)
  if (startCell === undefined || startRow === undefined) return

  const maxRows = outputItems.value.length
  const maxCols = outputColumns.value.length

  const isSingleCellPaste = pasteData.length === 1 && pasteData[0].length === 1
  const hasRangeSelection = rangeEnd !== rangeStart

  if (isSingleCellPaste && hasRangeSelection) {
    const pasteValue = pasteData[0][0]

    let selectionShift = rangeStart + cols
    for (let cellIndex = rangeStart; cellIndex <= rangeEnd; cellIndex++) {
      if (cellIndex === selectionShift) {
        cellIndex += step
        selectionShift += (step + cols)
      }
      const { cell, row } = calculateSelectIndexes(cellIndex)

      const ref = getRowRef(row)
      if (!ref) continue

      ref.paste(cell, pasteValue)
    }

    emit('pasted')
    return
  }

  const pasteRows = Math.min(pasteData.length, maxRows - startRow + 1)
  const pasteCols = Math.min(
    Math.max(...pasteData.map(row => row.length)),
    maxCols - startCell
  )

  let endCell = startCell
  let endRow = startRow

  for (let rowOffset = 0; rowOffset < pasteRows; rowOffset++) {
    const targetRow = startRow + rowOffset
    const ref = getRowRef(targetRow)
    if (!ref) continue

    const pasteRow = pasteData[rowOffset]
    for (let colOffset = 0; colOffset < pasteCols; colOffset++) {
      const targetCell = startCell + colOffset
      if (targetCell >= maxCols) break

      const pasteValue = pasteRow[colOffset]
      if (pasteValue !== undefined) {
        ref.paste(targetCell, pasteValue)
        endCell = Math.max(endCell, targetCell)
        endRow = Math.max(endRow, targetRow)
      }
    }
  }

  // Update selection so pasted data is highlighted
  const totalCols = outputColumns.value.length - silentColumnsCount
  const newRangeEnd = rangeStart + (endRow - startRow) * totalCols + (endCell - startCell)
  selectionService.range.end = newRangeEnd

  emit('pasted')
}

const startInput = (data: string) => {
  if (!canEditCells.value) return
  if (!selectionService.hasSelected.value) return
  const range = selectionService.range
  if (!range.start || range.start !== range.end) return
  const { cell, row } = calculateSelectIndexes(range.start)
  const ref = getRowRef(row)
  if (!cell || !row || !ref) return
  ref.paste(cell, data, false)
  edit.cell = cell
  edit.row = row
}

if (props.editable) {
  const activeElement = useActiveElement()
  const isUsingInput = computed(
    () =>
      activeElement.value?.tagName === 'INPUT' ||
      activeElement.value?.tagName === 'TEXTAREA',
  )

  useMagicKeys({
    passive: false,
    onEventFired(e) {
      if (edit.cell && edit.row) return
      if (isUsingInput.value || e.type !== 'keydown') return
      if (e.key === 'Enter') {
        handleDblClickCell()
      }
      if (/^[a-z0-9]$/i.test(e.key) && !e.metaKey) {
        startInput(e.key)
      }
    },
  })
}

watch(isScrolledDown, (value, prev) => {
  if (!props.lazy || prev || !value) return
  itemsPerPage.value += props.lazy
})

watch(indeterminate, (value, prev) => {
  if (value || !prev) return
  checked.value = !!selected.value?.length
})

watch(
  () => props.editRowId,
  value => {
    if (!value) return
    selectionService?.reset()
  },
)

function release() {
  edit.cell = undefined
  edit.row = undefined
}

onClickOutside(containerRef, event => {
  const path =
    (event as any).path || (event.composedPath && event.composedPath())
  if (
    path.find((item: HTMLElement) =>
      item.classList?.contains('v-popper__popper'),
    )
  )
    return
  release()
})

onBeforeMount(() => {
  itemsPerPage.value = props.lazy || 0
})

onMounted(() => {
  containerRef.value?.addEventListener('scroll', clearInsertButton, {
    passive: true,
  })
})

onBeforeUnmount(() => {
  containerRef.value?.removeEventListener('scroll', clearInsertButton)
})

if (props.valuePaster !== undefined) {
  onBeforeMount(() => {
    document.addEventListener('paste', handlePaste)
  })

  onBeforeUnmount(() => {
    document.removeEventListener('paste', handlePaste)
  })
}
</script>

<style scoped>
.ui-grid {
  @apply flex flex-col flex-auto;
  @apply relative;
  @apply select-none;
  @apply overflow-hidden;

  &--wrapped {
    @apply -mx-4 sm:-mx-default;
  }

  &__container--no-result {
    @apply p-0! flex-none!;
    @apply overflow-hidden!;
  }

  &__lazy {
    @apply h-px;
  }

}

.ui-grid:not(.ui-grid--collapsed) {
  .ui-grid__container {
    @apply grid content-start;
    @apply overflow-auto;
  }
}

.ui-grid--collapsed {
  .ui-grid__container {
    @apply flex-auto;
    @apply px-3 py-2;
    @apply bg-gray-100 dark:bg-gray-800;
    @apply rounded-b-md;
    @apply overflow-auto;
  }
}
</style>
